import React, { useContext, useEffect } from "react";
import { RotatingLines } from 'react-loader-spinner';
import { createUseStyles } from "react-jss";
import { EmpdContext } from '../../../../../Context'
import DashboardCard from "./DashboardCard";
import classNames from "classnames";
import Button from "../../../../../Components/Button";
import { questionSquare } from "../../../../../Assets/Images";

interface Props {
  className?: string;
  onSetResearchParticipant: (value: boolean) => void;
}

const useStyles = createUseStyles({
  root: {},
  diagnosedButton: {
    flexBasis: "41%",
    textAlign: "center",
  },
  diagnosedQuestion: {
    padding: 30,
    backgroundColor: "#f6bb6021",
    border: "1px solid #f6bb60",
    borderRadius: "5px",
  },
  diagnosedQuestionHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    "& h4": {
      fontSize: "1.35em",
      margin: 0,
    },
  },
  notDiagnosedButton: {
    backgroundColor: "#e1e5ec !important",
    flexBasis: "41%",
    textAlign: "center",
  },
  diagnosedContainer: {
    marginTop: "50px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  questionMark: {
    height: 25,
    width: "auto",
    marginRight: 15,
  },
});

const useMyContext = () => {
    const context = useContext(EmpdContext)
    if( !context ) {
        throw new Error("No provider found for Context")
    }
    return context
}

const CaptureConsentCard = ({ className, onSetResearchParticipant }: Props) => {
  const context = useMyContext()
  const { isLoading, isRedirecting, hasBeenDiagnosed, setHasBeenDiagnosed, writeFlags, patientResult, setDeclinedDialogOpen, schemaName, demoTable } = context
  const classes = useStyles();

  useEffect(() => {
        if( Object.keys(patientResult).length > 0 &&  patientResult.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.included`).value === null ) {
            const included = patientResult.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.included`).value === 'True' ? true : false
          if( hasBeenDiagnosed && !included) {
            writeFlags({[`${schemaName}.${demoTable}.included`]: true})
          }
        }
  }, [hasBeenDiagnosed])

  const questionLabel = hasBeenDiagnosed
      ? "Would you like to participate in research?"
      : "Have you ever been diagnosed with EMPD?";

  const yesButtonLabel = hasBeenDiagnosed
    ? "Yes, I'd like to participate"
    : `Yes, I've been diagnosed`;

  const noButtonLabel = hasBeenDiagnosed
    ? "No, thanks"
    : `No, I've NOT been diagnosed`;

  const questionDesc = hasBeenDiagnosed
    ? <div>Participating in EMPD research helps advance treatment options and bring greater awareness to this rare disease. Your experience can lead to earlier diagnoses, improved care, and real hope for future patients. Every voice matters—and yours could make all the difference.<br/>
			<h3>Before You Continue</h3>
We’re glad you’re here. Before you sign the consent form, here’s a quick look at what this research study involves so you can feel comfortable and informed:
			<ul>
				<li>We’re collecting basic health information, samples, and images to help researchers better understand Extramammary Paget’s Disease (EMPD).</li>
				<li><strong>Your privacy matters to us.</strong> We do our best to keep your information safe and secure. However, no method of online transmission is completely risk-free.</li>
				<li>Only approved teams will be able to access your data, and we follow strict privacy laws, including HIPAA.</li>
				<li>You will not receive extra medical tests for this study.</li>
				<li>There is no direct benefit or payment for taking part.</li>
				<li><strong>Joining is completely your choice.</strong> Saying no will not affect your care in any way.</li>
				<li>You can change your mind and stop at any time.</li>
			</ul>
		If you choose to continue, you’ll see the full consent form with all the details
	</div>
    : "Have you ever been diagnosed with Extramammary Paget's Disease (EMPD) through a biopsy? Your experience could play a vital role in advancing research and improving care for others facing this rare condition.";

  const handleHasBeenDiagnosedClick = (value: boolean) => {

	if( !value) {
		setDeclinedDialogOpen(true)
	}

    if (hasBeenDiagnosed) {
      onSetResearchParticipant(value);
      return;
    }

    setHasBeenDiagnosed(value);
	writeFlags({[`${schemaName}.${demoTable}.included`]: value})

  };

  if( isLoading ) {
	return (
		<DashboardCard
			className={classNames(classes.root, className)}
	    >
			<RotatingLines
				strokeColor="#152E54"
	            strokeWidth="3"
    	        animationDuration="1.0"
        	    width="48"
            	visible={true}
        	/>
		</DashboardCard>
	)
  } else {
  return (
    <DashboardCard
      title={"Inclusion + Consent"}
      className={classNames(classes.root, className)}
    >
	  {isLoading && (
		<RotatingLines
   	    	strokeColor="#152E54"
       	    strokeWidth="3"
            animationDuration="1.0"
   	        width="48"
       	    visible={true}
        />
	  )}
      {!isLoading && (
      <div className={classes.diagnosedQuestion}>
        <div className={classes.diagnosedQuestionHeader}>
		  	<img
            	src={questionSquare}
	            alt="question-mark"
    	        className={classes.questionMark}
        	  />
          <h4>{questionLabel}</h4>
        </div>
        <p>{questionDesc}</p>
	        <div className={classes.diagnosedContainer}>
    	      <Button
        	    className={classes.diagnosedButton}
	            label={yesButtonLabel}
    	        onClick={() => handleHasBeenDiagnosedClick(true)}
        	    loading={isRedirecting}
	          >
		  		<RotatingLines
        	    	strokeColor="#152E54"
   	        	    strokeWidth="3"
           	        animationDuration="1.0"
               	    width="48"
                    visible={true}
   	            />
        	  </Button>
	          <Button
    	        className={classes.notDiagnosedButton}
        	    label={noButtonLabel}
            	onClick={() => handleHasBeenDiagnosedClick(false)}
          	  />
        	</div>
      </div>
      )}
    </DashboardCard>
  );
	}
};

export default CaptureConsentCard;
